/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type EffortJudgement from '@linc-technologies/linced-common/enums/effort-judgement';
import type ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import type ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import type GoalSubject from '@linc-technologies/linced-common/models/goals/subject';
import type Level from '@linc-technologies/linced-common/models/milestones/level';
import type Milestone from '@linc-technologies/linced-common/models/milestones/milestone';
import type Person from '@linc-technologies/linced-common/models/person';
import type Page from '@linc-technologies/linced-common/models/posts/page';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: milestones.Progression.
export default class Progression extends Model {
  declare [Type]: 'milestones/progression';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  @belongsTo('person', { async: true, inverse: null })
  declare assessorId: AsyncBelongsTo<Person>;

  // TS-BUG: assessor aliases personId (the learner), not assessorId.
  @alias('personId')
  declare assessor: AsyncBelongsTo<Person>;

  @belongsTo('milestones/milestone', { async: true, inverse: null })
  declare milestoneId: AsyncBelongsTo<Milestone>;

  @alias('milestoneId')
  declare milestone: AsyncBelongsTo<Milestone>;

  @alias('milestone.id')
  declare milestone_id: string | null | undefined;

  // No defaultValue: callers give dueDate to moment(). moment(undefined) is
  // now, but moment('') is an invalid date.
  @attr('string')
  declare dueDate: string | undefined;

  @attr('number', { defaultValue: 0 })
  declare progressionType: EnumValue<typeof ProgressionType> | 0;

  @belongsTo('posts/page', { async: true, inverse: null })
  declare pageId: AsyncBelongsTo<Page>;

  @alias('pageId')
  declare page: AsyncBelongsTo<Page>;

  @alias('page.id')
  declare page_id: string | null | undefined;

  @belongsTo('goals/subject', { async: true, inverse: null })
  declare goalSubjectId: AsyncBelongsTo<GoalSubject>;

  @alias('goalSubjectId')
  declare goalSubject: AsyncBelongsTo<GoalSubject>;

  @alias('goalSubject.id')
  declare goal_subject_id: string | null | undefined;

  @belongsTo('milestones/level', { async: true, inverse: null })
  declare levelId: AsyncBelongsTo<Level>;

  @alias('levelId')
  declare level: AsyncBelongsTo<Level>;

  // 0 (unspecified) means no judgement. Callers check for `> 0`.
  @attr('number', { defaultValue: 0 })
  declare judgement: EnumValue<typeof ProgressionJudgement>;

  // The proto type is Judgement, but the values are the effort scale (1 to 5).
  // 0 means no effort.
  @attr('number', { defaultValue: 0 })
  declare effort: EnumValue<typeof EffortJudgement> | 0;

  @attr('string', { defaultValue: '' })
  declare elementId: string;
}
